import { useState, useEffect, useRef } from 'react';
import { useApiToast } from 'components/Toaster';
import { AgendaEvent } from 'services/agenda/agenda.types';
import { useGetPersonsMutation } from 'services/person/person.endpoint';
import { Person } from 'services/person/person.types';
import { useCreateAgendaEventMutation } from 'services/agenda/agenda.endpoint';
import { useSelector } from 'react-redux';
import { getDatalists } from 'features/datalist';
import { useGetPartnersMutation } from 'services/partner/partner.endpoint';
import { Partner } from 'services/partner/partner.types';
import { useCalendar } from '../useCalendar';
import moment from 'moment';

type UseCreateEventModalVMProps = {
    onEventCreated?: (event: AgendaEvent) => void;
};

export default function useCreateEventModalVM({ onEventCreated }: UseCreateEventModalVMProps = {}) {
    const [isUserCreateAppointmentModalOpen, setIsUserCreateAppointmentModalOpen] = useState<boolean>(false);
    const [persons, setPersons] = useState<Person[]>([]);
    const [partners, setPartners] = useState<Partner[]>([]);
    const { setOpenCreateEventModal } = useCalendar();

    const [
        getPersons,
        {
            isLoading: isGetPersonsLoading,
            isError: isGetPersonsError,
            isSuccess: isGetPersonsSuccess,
            error: getPersonsError,
            data: getPersonsData
        }
    ] = useGetPersonsMutation();

    const [
        getPartners,
        {
            isLoading: isGetPartnersLoading,
            isError: isGetPartnersError,
            isSuccess: isGetPartnersSuccess,
            error: getPartnersError,
            data: getPartnersData
        }
    ] = useGetPartnersMutation();

    const [
        createAgendaEvent,
        {
            isLoading: isCreateAgendaEventLoading,
            isError: isCreateAgendaEventError,
            isSuccess: isCreateAgendaEventSuccess,
            error: createAgendaEventError,
            data: createAgendaEventData
        }
    ] = useCreateAgendaEventMutation();

    const { showApiToast, showErrorToast } = useApiToast();

    useEffect(() => {
        getPersons(undefined);
    }, [getPersons]);

    useEffect(() => {
        getPartners(undefined);
    }, [getPartners]);

    const handleCreateEvent = (values: any) => {
        ['stakeholders', 'assistants', 'partners'].forEach((key) => {
            if (values[key]) {
                values[key] = values[key].map((item: any) => Number(item.value));
            }
        });

        if (values.person && values.person.value) {
            values.person_id = values.person.value;
        }

        if (values.stakeholders_list) {
            values.stakeholders = values.stakeholders_list.map((item: any) => Number(item.value));
        }
        if (values.assistants_list) {
            values.assistants = values.assistants_list.map((item: any) => Number(item.value));
        }
        if (values.partners_list) {
            values.partners = values.partners_list.map((item: any) => Number(item.value));
        }
        if (values.emails_list) {
            values.emails = values.emails_list.map((item: any) => item.value);
        }

        if (values.person && values.person.value) {
            values.person_id = Number(values.person.value);
        }

        if (values.created_by && values.created_by.value) {
            values.created_by_id = Number(values.created_by.value);
        }

        if (moment(values.started_at).isAfter(moment(values.ended_at))) {
            [values.start, values.end] = [values.end, values.start];
            [values.started_at, values.ended_at] = [values.ended_at, values.started_at];
            [values.start_at_date, values.end_at_date] = [values.end_at_date, values.start_at_date];
            [values.start_at_hour, values.end_at_hour] = [values.end_at_hour, values.start_at_hour];
            [values.start_at_minute, values.end_at_minute] = [values.end_at_minute, values.start_at_minute];
        }

        createAgendaEvent(values);
    };

    useEffect(() => {
        if (isCreateAgendaEventLoading) return;
        if (isCreateAgendaEventError && createAgendaEventError) {
            showErrorToast(createAgendaEventError, {
                message: "Erreur lors de la création de l'évènement"
            });
        }
        if (isCreateAgendaEventSuccess && createAgendaEventData) {
            showApiToast(createAgendaEventData, {
                successMessage: 'Évènement créé avec succès',
                errorMessage: "Erreur lors de la création de l'évènement"
            });

            setIsUserCreateAppointmentModalOpen(false);
            setOpenCreateEventModal(false);
            onEventCreated?.(createAgendaEventData.data);
        }
    }, [
        isCreateAgendaEventLoading,
        isCreateAgendaEventError,
        isCreateAgendaEventSuccess,
        createAgendaEventError,
        createAgendaEventData,
        setOpenCreateEventModal
    ]);

    useEffect(() => {
        if (isGetPersonsLoading) return;
        if (isGetPersonsError && getPersonsError) {
            showErrorToast(getPersonsError, {
                message: 'Erreur lors de la récupération des personnes'
            });
        }
        if (isGetPersonsSuccess && getPersonsData) {
            setPersons([...getPersonsData.data].sort((a, b) => a.firstname.localeCompare(b.firstname)));
        }
    }, [isGetPersonsLoading, isGetPersonsError, isGetPersonsSuccess, getPersonsError, getPersonsData]);

    useEffect(() => {
        if (isGetPartnersLoading) return;
        if (isGetPartnersError && getPartnersError) {
            showErrorToast(getPartnersError, {
                message: 'Erreur lors de la récupération des partenaires'
            });
        }
        if (isGetPartnersSuccess && getPartnersData) {
            setPartners([...getPartnersData.data].sort((a, b) => a.name.localeCompare(b.name)));
        }
    }, [isGetPartnersLoading, isGetPartnersError, isGetPartnersSuccess, getPartnersError, getPartnersData]);

    return {
        isUserCreateAppointmentModalOpen,
        setIsUserCreateAppointmentModalOpen,
        handleCreateEvent,
        persons,
        partners
    };
}
